import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { FC, ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { border } from '../../layout/theme/border'
import { inkColor, themeColor } from '../../layout/theme/color'
import { fadeSlideIn } from './animations'

interface Crumb {
  label: ReactNode
  to?: string
}

interface PageHeaderProps {
  crumbs?: Crumb[]
  /** Visual left of the title (tile, icon). */
  leading?: ReactNode
  title: ReactNode
  /** Pills next to the title. */
  badges?: ReactNode
  /** One muted line below the title. */
  subtitle?: ReactNode
  /** Page-level actions, right aligned. */
  actions?: ReactNode
}

/**
 * Page header: breadcrumb, title with badges, subtitle and actions.
 *
 *   Certificaten > …
 *   [tile] Title  (pill) (pill)                [action] [⋯]
 *          subtitle · meta
 */
export const PageHeader: FC<PageHeaderProps> = ({ crumbs, leading, title, badges, subtitle, actions }) => (
  <Box component="header" sx={{ mb: 3, animation: `${fadeSlideIn} 0.3s ease-out both` }}>
    {crumbs && crumbs.length > 0 && (
      <Box
        component="nav"
        aria-label="breadcrumb"
        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1.25, minHeight: 22 }}
      >
        {crumbs.map((crumb, index) => (
          <Box key={index} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            {index > 0 && <ChevronRightIcon sx={{ fontSize: 16, color: themeColor.slate40 }} />}
            {crumb.to ? (
              <Box
                component={RouterLink}
                to={crumb.to}
                sx={{
                  'display': 'inline-flex',
                  'alignItems': 'center',
                  'gap': 0.75,
                  'fontSize': 13,
                  'fontWeight': 500,
                  'color': themeColor.slate50,
                  'textDecoration': 'none',
                  'borderRadius': `${border.radius.sm}px`,
                  'px': 0.5,
                  'mx': -0.5,
                  '&:hover': { color: themeColor.slate, bgcolor: alpha(inkColor, 0.04) },
                }}
              >
                {crumb.label}
              </Box>
            ) : (
              <Box component="span" sx={{ fontSize: 13, color: themeColor.slate60 }}>{crumb.label}</Box>
            )}
          </Box>
        ))}
      </Box>
    )}
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" justifyContent="space-between">
      <Stack direction="row" spacing={1.75} alignItems="center" sx={{ minWidth: 0 }}>
        {leading}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} useFlexGap flexWrap="wrap">
            <Typography
              component="h1"
              sx={{
                fontSize: 26,
                lineHeight: '34px',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: themeColor.slate,
                overflowWrap: 'anywhere',
              }}
            >
              {title}
            </Typography>
            {badges}
          </Stack>
          {subtitle && (
            <Box sx={{ mt: 0.5, fontSize: 13, color: themeColor.slate50, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {subtitle}
            </Box>
          )}
        </Box>
      </Stack>
      {actions && (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ flexShrink: 0, pt: { md: 0.25 } }}>
          {actions}
        </Stack>
      )}
    </Stack>
  </Box>
)
